import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRightLeft } from 'lucide-react';
import Card from '../../components/UI/Card';
import './UnitConverter.css';

const CONVERSION_RATES = {
    length: {
        meters: 1,
        kilometers: 0.001,
        centimeters: 100,
        millimeters: 1000,
        miles: 0.000621371,
        yards: 1.09361,
        feet: 3.28084,
        inches: 39.3701
    },
    weight: {
        kilograms: 1,
        grams: 1000,
        milligrams: 1000000,
        pounds: 2.20462,
        ounces: 35.274
    },
    temperature: {
        celsius: 'c',
        fahrenheit: 'f',
        kelvin: 'k'
    }
};

const UnitConverter = () => {
    const [category, setCategory] = useState('length');
    const [fromUnit, setFromUnit] = useState('meters');
    const [toUnit, setToUnit] = useState('feet');
    const [fromValue, setFromValue] = useState(1);
    const [toValue, setToValue] = useState('');

    useEffect(() => {
        // Reset units when category changes
        const units = Object.keys(CONVERSION_RATES[category]);
        setFromUnit(units[0]);
        setToUnit(units[1]);
    }, [category]);

    useEffect(() => {
        convert();
    }, [fromValue, fromUnit, toUnit, category]);

    const convert = () => {
        // Guard: Ensure units belong to current category
        if (!CONVERSION_RATES[category][fromUnit] || !CONVERSION_RATES[category][toUnit]) {
            return;
        }

        if (category === 'temperature') {
            convertTemperature();
        } else {
            const valueInBase = fromValue / CONVERSION_RATES[category][fromUnit];
            const result = valueInBase * CONVERSION_RATES[category][toUnit];
            setToValue(parseFloat(result.toFixed(6)));
        }
    };

    const convertTemperature = () => {
        let celsius;
        // Convert to Celsius first
        if (fromUnit === 'celsius') celsius = fromValue;
        if (fromUnit === 'fahrenheit') celsius = (fromValue - 32) * 5 / 9;
        if (fromUnit === 'kelvin') celsius = fromValue - 273.15;

        // Convert from Celsius to target
        let result;
        if (toUnit === 'celsius') result = celsius;
        if (toUnit === 'fahrenheit') result = (celsius * 9 / 5) + 32;
        if (toUnit === 'kelvin') result = celsius + 273.15;

        setToValue(parseFloat(result.toFixed(2)));
    };

    const handleSwap = () => {
        setFromUnit(toUnit);
        setToUnit(fromUnit);
    };

    return (
        <div className="tool-page container">
            <Helmet>
                <title>Unit Converter - Length, Weight, Temperature | OnlineToolSphere</title>
                <meta name="description" content="Free online unit converter. Convert length, weight, and temperature units instantly. Supports metric and imperial systems." />
                <meta property="og:title" content="Unit Converter - Length, Weight, Temperature | OnlineToolSphere" />
                <meta property="og:description" content="Free online unit converter. Convert length, weight, and temperature units instantly. Supports metric and imperial systems." />
                <meta property="og:url" content="https://onlinetoolsphere.com/tools/unit-converter/" />
                <meta name="twitter:title" content="Unit Converter - Length, Weight, Temperature | OnlineToolSphere" />
                <meta name="twitter:description" content="Free online unit converter. Convert length, weight, and temperature units instantly. Supports metric and imperial systems." />
            </Helmet>
            <div className="tool-header">
                <h1 className="text-gradient">Unit Converter</h1>
                <p className="tool-subtitle">Convert between common units of measurement.</p>
            </div>

            <Card className="converter-card">
                <div className="category-tabs">
                    {Object.keys(CONVERSION_RATES).map((cat) => (
                        <button
                            key={cat}
                            className={`tab-btn ${category === cat ? 'active' : ''}`}
                            onClick={() => setCategory(cat)}
                        >
                            {cat.charAt(0).toUpperCase() + cat.slice(1)}
                        </button>
                    ))}
                </div>

                <div className="converter-grid">
                    <div className="input-group">
                        <label>From</label>
                        <input
                            type="number"
                            value={fromValue}
                            onChange={(e) => setFromValue(parseFloat(e.target.value) || 0)}
                        />
                        <select value={fromUnit} onChange={(e) => setFromUnit(e.target.value)}>
                            {Object.keys(CONVERSION_RATES[category]).map((unit) => (
                                <option key={unit} value={unit}>
                                    {unit.charAt(0).toUpperCase() + unit.slice(1)}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="swap-icon" onClick={handleSwap} style={{ cursor: 'pointer' }}>
                        <ArrowRightLeft size={24} />
                    </div>

                    <div className="input-group">
                        <label>To</label>
                        <input
                            type="number"
                            value={toValue}
                            readOnly
                            className="readonly-input"
                        />
                        <select value={toUnit} onChange={(e) => setToUnit(e.target.value)}>
                            {Object.keys(CONVERSION_RATES[category]).map((unit) => (
                                <option key={unit} value={unit}>
                                    {unit.charAt(0).toUpperCase() + unit.slice(1)}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default UnitConverter;
